// The chat-top forms surface. For a single-form tenant with nothing submitted
// yet, this is exactly the pinned card, byte-for-byte — every multiform
// affordance (menu control, Up next rows, the collapsed Your Forms bar)
// exists only once the registry holds more than one browsable form, so absent
// `BotForms` nothing changes pre-submit.
//
// Forms-corrections slice (spec amendment 2026-08-16): the menu is also the
// review entry point (its Submitted row carries Review), and that entry SHALL
// exist for every tenant shape — a single-form tenant is not exempt just
// because it never had a menu before corrections existed. So the gate below
// admits a second condition: once at least one browsable form is submitted,
// the menu surfaces appear regardless of browsable-form count. A tenant with
// nothing submitted and fewer than two browsable forms is unaffected.
//
// Anatomy with multiform (delta spec):
// - the pinned card carries the menu toggle;
// - dashed "Up next: {Form}" rows sit under it, one per queued form, each with
//   Switch and a quiet remove;
// - when no form is active but forms history exists, a collapsed
//   "Your Forms · n" bar stands in for the card;
// - the menu expands inline (pushes content — design D3), listing every form.

import { useState } from 'react';
import styles from './ConversationalForms.module.css';
import FormsMenuRows from './FormsMenu';
import { useConversationalForms } from './FormsContext';
import { formLifecycle } from './formsSelectors';
import { ChevronDownIcon, ClipboardIcon, CloseIcon } from './icons';
import PinnedFormCard from './PinnedFormCard';

const FormsHeader = () => {
    const { active, registry, state, openFormView, switchToForm, removeQueuedForm } =
        useConversationalForms();
    const [menuOpen, setMenuOpen] = useState(false);

    if (!active || !registry) return <PinnedFormCard onOpen={openFormView} />;
    // Counted over *browsable* forms (store tracker 6.30): ambient forms are
    // never visitor-startable, so a tenant whose only extra form is ambient
    // stays on the single-form surface unless it has itself been submitted —
    // or (store tracker 6.55) paused: pausing a single-form tenant's only
    // form puts it in `queuedForms` with nothing active, and without this
    // check the multiform menu/Up-next surfaces that are its only way back
    // would never mount, stranding the visitor with no resume path at all.
    const browsableForms = registry.forms.filter((f) => f.browsable);
    const hasHistory = browsableForms.some((f) => {
        const lifecycle = formLifecycle(state, f.objectId);
        return lifecycle === 'submitted' || lifecycle === 'queued';
    });
    if (browsableForms.length < 2 && !hasHistory) {
        return <PinnedFormCard onOpen={openFormView} />;
    }

    // 2.3 (store tracker 6.55): at most one visible "Up next" row — the
    // front of the queue, the most likely resume — with any further queued
    // forms reachable only through the forms menu below.
    const queued = state.queuedForms
        .map((id) => registry.byId.get(id))
        .filter((f): f is NonNullable<typeof f> => f !== undefined)
        .slice(0, 1);

    // Forms with a live claim on attention while nothing is pinned: waiting in
    // the queue or already submitted (the prototype's queued + done count).
    const historyCount = registry.forms.filter((f) => {
        const lifecycle = formLifecycle(state, f.objectId);
        return lifecycle === 'queued' || lifecycle === 'submitted';
    }).length;

    const hasActive = state.activeFormId !== null;

    return (
        <>
            <PinnedFormCard
                onOpen={openFormView}
                onToggleMenu={() => setMenuOpen((open) => !open)}
                menuOpen={menuOpen}
            />

            {!hasActive && historyCount > 0 && (
                <button
                    type="button"
                    className={styles.formsBar}
                    onClick={() => setMenuOpen((open) => !open)}
                    aria-expanded={menuOpen}
                    data-testid="cf-forms-bar"
                >
                    <ClipboardIcon size={14} />
                    <span className={styles.formsBarLabel}>Your Forms</span>
                    <span className={styles.formsBarCount}>{historyCount}</span>
                    <ChevronDownIcon size={13} className={menuOpen ? styles.formsBarOpen : undefined} />
                </button>
            )}

            {menuOpen && (
                <div className={styles.menuPanel}>
                    <FormsMenuRows onAction={() => setMenuOpen(false)} />
                </div>
            )}

            {queued.map((form) => (
                <div key={form.objectId} className={styles.upNextRow} data-testid="cf-upnext-row">
                    <span className={styles.upNextLabel}>
                        Up next: <strong>{form.short}</strong>
                    </span>
                    <button
                        type="button"
                        className={styles.upNextSwitch}
                        aria-label={`Switch to ${form.short}`}
                        onClick={() => switchToForm(form.objectId)}
                    >
                        Switch
                    </button>
                    <button
                        type="button"
                        className={styles.upNextRemove}
                        aria-label={`Remove ${form.short}`}
                        onClick={() => removeQueuedForm(form.objectId)}
                    >
                        <CloseIcon size={12} />
                    </button>
                </div>
            ))}
        </>
    );
};

export default FormsHeader;
